"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CommunityLinks, type CommunityPlatform } from "@/components/community/CommunityLinks";
import { captureClientEvent } from "@/lib/analytics/client";

export default function CommunityLinksModal() {
  const { isAuthenticated } = useConvexAuth();
  const pathname = usePathname();
  const onDashboard = pathname?.startsWith("/dashboard") ?? false;
  const onboarding = useQuery(
    api.onboarding.getMyOnboarding,
    isAuthenticated ? {} : "skip",
  );
  const markSeen = useMutation(api.onboarding.markCommunityLinksSeen);

  const [open, setOpen] = useState(false);
  const latchedRef = useRef(false);

  // Show once, on the dashboard, after onboarding is complete and only if never
  // seen. Latch so a reactive query update (after we mark it seen) can't re-open
  // or flash it. New users see the community step inside onboarding instead
  // (which marks it seen), so this only ever fires for already-onboarded users.
  useEffect(() => {
    if (latchedRef.current) return;
    if (!onDashboard) return;
    if (!onboarding) return;
    if (!onboarding.completed) return;
    if (onboarding.communityLinksSeenAt !== undefined) return;
    latchedRef.current = true;
    setOpen(true);
  }, [onDashboard, onboarding]);

  const dismiss = useCallback(() => {
    setOpen(false);
    markSeen({}).catch(() => {});
  }, [markSeen]);

  const handleJoin = useCallback(
    (platform: CommunityPlatform) => {
      captureClientEvent("community_modal_join", { platform, source: "modal" });
    },
    [],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) dismiss();
    },
    [dismiss],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90dvh] w-full max-w-2xl overflow-y-auto">
        <DialogTitle className="sr-only">Join the community</DialogTitle>
        <CommunityLinks onJoin={handleJoin} />
        <Button
          onClick={dismiss}
          className="mt-2 h-12 w-full rounded-full font-polysans text-base font-semibold"
        >
          Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
}
